import { QueryKey } from '@tanstack/react-query'
import { SupportedChainId } from 'config/chains'
import { SubgraphType } from 'config/subgraph'
import type { SubgraphQueryOptions } from 'hooks/subgraph/queryOptions'
import { useSubgraphConfig } from 'hooks/useSubgraphConfig'
import {
  GetVaultHistoryQuery,
  GqlChain,
  GqlRewardVaultSnapshotDataRange,
  GqlRewardVaultSnapshotResolution,
  useGetVaultHistoryQuery,
} from 'queries/berachain/generated'
import { useMemo } from 'react'

const berachainKeyBase = () => ['berachain', SupportedChainId.BERA] as const

type VaultAprHistoryPoint = GetVaultHistoryQuery['polGetRewardVaultSnapshots'][number]
type VaultAprHistoryQueryOptions = SubgraphQueryOptions<VaultAprHistoryPoint[], GetVaultHistoryQuery>

type NormalizedVaultAprHistoryParams = {
  chain: GqlChain
  vaultId: string
  resolution: GqlRewardVaultSnapshotResolution
  range: GqlRewardVaultSnapshotDataRange
}

export type VaultAprHistoryParams = {
  vaultId?: string
  resolution?: GqlRewardVaultSnapshotResolution
  range?: GqlRewardVaultSnapshotDataRange
}

export const berachainQueryKeys = {
  vaultAprHistory: (params: NormalizedVaultAprHistoryParams) =>
    [...berachainKeyBase(), 'vaultAprHistory', params.chain, params.vaultId, params.resolution, params.range] as const,
}

export const useVaultAprHistory = (params: VaultAprHistoryParams, options?: VaultAprHistoryQueryOptions) => {
  const { endpoint } = useSubgraphConfig(SubgraphType.BERACHAIN)
  const dataSource = useMemo(
    () => ({
      endpoint,
      fetchParams: {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'x-apollo-operation-name': 'GetVaultHistory',
          'apollo-require-preflight': 'true',
          'x-graphql-client-name': 'Kodiak',
        },
      },
    }),
    [endpoint]
  )

  const normalizedParams = useMemo(
    () => ({
      chain: GqlChain.Berachain,
      vaultId: params.vaultId?.toLowerCase() ?? '',
      resolution: params.resolution ?? GqlRewardVaultSnapshotResolution.Day,
      range: params.range ?? GqlRewardVaultSnapshotDataRange.NinetyDays,
    }),
    [params.vaultId, params.resolution, params.range]
  )

  const {
    skip,
    enabled: optionEnabled,
    queryKey,
    retry = false,
    staleTime = Infinity,
    gcTime = Infinity,
    refetchOnMount = false,
    refetchOnWindowFocus = false,
    refetchOnReconnect = false,
    ...queryOptions
  } = options ?? {}
  const enabled = (optionEnabled ?? true) && !skip && Boolean(normalizedParams.vaultId)

  return useGetVaultHistoryQuery<VaultAprHistoryPoint[], Error>(dataSource, normalizedParams, {
    queryKey: (queryKey ?? berachainQueryKeys.vaultAprHistory(normalizedParams)) as QueryKey,
    select: (data) => data.polGetRewardVaultSnapshots,
    enabled,
    retry,
    staleTime,
    gcTime,
    refetchOnMount,
    refetchOnWindowFocus,
    refetchOnReconnect,
    ...queryOptions,
  })
}
